import type { LandingPageContent } from '../types'

export const JAK_OBLICZAMY_CENE: LandingPageContent = {
  slug: 'jak-obliczamy-cene',
  lang: 'pl',
  topicId: 'methodik-preisberechnung',
  title: 'Jak Roofora oblicza cenę? Metodologia i dane',
  metaDescription:
    'Skąd bierze się średnia cena w kalkulatorze kosztów? Baza danych, poziomy wiarygodności i ' +
    'częstotliwość aktualizacji wyjaśnione transparentnie - bez zmyślonych liczb, bez fałszywych ocen.',
  keywords: [
    'metodologia kalkulatora fotowoltaicznego',
    'jak Roofora oblicza cenę',
    'dane szacowanie ceny fotowoltaiki',
    'Roofora obliczanie ceny średniej',
    'orientacja fotowoltaiki wydajność',
    'częściowe zacienienie utrata wydajności',
    'orientacja dachu fotowoltaika',
  ],
  eyebrow: 'Przejrzystość',
  h1: 'Jak Roofora oblicza cenę?',
  intro:
    'Średnia cena w kalkulatorze kosztów nie jest stałą tabelą ani szacunkiem samej Roofora, tylko ' +
    'jest obliczana na nowo przy każdym zapytaniu na podstawie prawdziwych ofert firm ' +
    'rzemieślniczych złożonych na platformie. Ta strona wyjaśnia, jakie dane wchodzą w grę, jak ' +
    'obliczenia radzą sobie z wciąż małą lub rosnącą ilością danych oraz czego ten kalkulator ' +
    'świadomie nie robi.',
  sections: [
    { type: 'heading', text: 'Baza danych: prawdziwe oferty, żadnych zmyślonych liczb' },
    {
      type: 'paragraph',
      text:
        'Każdy punkt danych pochodzi z prawdziwej oferty firmy rzemieślniczej złożonej w Roofora na ' +
        'kompletną instalację (fotowoltaiczną, opcjonalnie z magazynem energii). Nie ma minimalnej ' +
        'liczby ofert, od której funkcja dopiero się "odblokowuje", ani zaokrąglonych czy ' +
        'szacunkowych wartości zastępczych - baza danych rośnie z każdą nową ofertą, niezależnie od ' +
        'tego, jak mała jest w danym momencie.',
    },
    { type: 'heading', text: 'Cztery poziomy wiarygodności zamiast jednej liczby' },
    {
      type: 'list',
      items: [
        'Średnia regionalna: jeśli jest wystarczająco dużo porównywalnych ofert z Twojego regionu (przybliżony obszar kodu pocztowego), liczy się wyłącznie na ich podstawie.',
        'Średnia ogólnokrajowa: jeśli ofert regionalnych jest za mało, ale wystarczająco dużo porównywalnych ofert z całych Niemiec, pokazywana jest zamiast tego ta średnia - wyraźnie oznaczona jako "ogólnokrajowa".',
        'Zgrubne oszacowanie: jeśli nawet porównanie ogólnokrajowe nie wystarcza dla dokładnie takiej wielkości instalacji/magazynu energii, wyliczane jest bardziej ogólne oszacowanie na podstawie ceny za kWp lub za kWh magazynu ze wszystkich dotychczasowych ofert systemów kompletnych - wyraźnie oznaczone jako mniej dokładne.',
        'Za mało danych: jeśli i to nie wystarcza, pokazywany jest uczciwie komunikat "za mało danych" zamiast zmyślonej liczby. Mimo to można bezpłatnie i niezobowiązująco złożyć zapytanie.',
      ],
    },
    {
      type: 'paragraph',
      text:
        'Ta kolejność jest przechodzona na nowo przy każdym obliczeniu i nigdy nie jest po cichu ' +
        'mieszana - zawsze widać, na którym z czterech poziomów opiera się wynik, a nie tylko samą ' +
        'liczbę bez kontekstu.',
    },
    { type: 'heading', text: 'Aktualizacja' },
    {
      type: 'paragraph',
      text:
        'Nie ma osobnego cyklu aktualizacji - każde obliczenie sięga na żywo do aktualnego stanu ' +
        'wszystkich dotychczas złożonych ofert. Im więcej firm rzemieślniczych składa oferty przez ' +
        'Roofora, tym częściej stosowany jest dokładniejszy poziom regionalny zamiast ' +
        'ogólnokrajowego lub zgrubnego oszacowania.',
    },
    { type: 'heading', text: 'Czego ten kalkulator świadomie nie robi' },
    {
      type: 'list',
      items: [
        'Żadnych zmyślonych ani zaokrąglonych ocen/gwiazdek - dopóki nie ma prawdziwych, jawnych ocen użytkowników, żadna nie jest wyświetlana.',
        'Nie jest wiążącą ofertą: liczba jest szacunkiem na podstawie dotychczasowych ofert, a nie stałą ceną dla Twojego konkretnego dachu.',
        'Twój kod pocztowy ani inne dane nie są przekazywane osobom trzecim - sam kalkulator niczego nie zapisuje.',
      ],
    },
    { type: 'heading', text: 'Orientacja dachu: jak kalkulator szacuje wydajność' },
    {
      type: 'paragraph',
      text:
        'Oprócz ceny, także nasz bezpłatny kalkulator doboru instalacji działa według tej samej ' +
        'zasady przejrzystości: żadnych szacunkowych wartości zastępczych, tylko jawnie dostępna ' +
        'podstawa obliczeń. Dla orientacji dachu stosuje stały współczynnik wydajności dla każdej ' +
        'orientacji, z południem jako wartością referencyjną (100%):',
    },
    {
      type: 'list',
      items: [
        'Południe: 100% (wartość referencyjna)',
        'Południowy wschód / południowy zachód: 95%',
        'Wschód / zachód: 85%',
        'Dach płaski: 90%',
        'Północny wschód / północny zachód: 70%',
        'Północ: 60%',
      ],
    },
    {
      type: 'paragraph',
      text:
        'Te wartości są łączone z danymi nasłonecznienia dla lokalizacji (PVGIS), aby obliczyć ' +
        'zalecaną wielkość instalacji. Jeśli masz kilka połaci dachu o różnej orientacji (np. dach ' +
        'wschód-zachód), po prostu podaj każdą połać osobno z jej własną orientacją - kalkulator ' +
        'uwzględnia każdą z nich indywidualnie.',
    },
    { type: 'heading', text: 'Częściowe zacienienie: dlaczego nasze oszacowanie go nie uwzględnia' },
    {
      type: 'paragraph',
      text:
        'Częściowe zacienienie (np. przez drzewa, sąsiednie budynki lub kominy) może nieproporcjonalnie ' +
        'mocno obniżyć roczną wydajność - według zewnętrznych źródeł już 10% zacienienia połaci dachu ' +
        'może obniżyć roczną wydajność nawet o 15%. Szczerze mówiąc: bezpłatny kalkulator doboru ' +
        'instalacji nie modeluje zacienienia, jego oszacowanie opiera się wyłącznie na orientacji, ' +
        'kącie nachylenia i danych nasłonecznienia dla lokalizacji. Rzeczywistą ocenę zacienienia ' +
        'przeprowadza firma rzemieślnicza odpowiadająca na Twoje zapytanie, na podstawie zdjęć dachu ' +
        'lub wizji lokalnej - to wchodzi wtedy do jej indywidualnej oferty, a nie do wstępnego ' +
        'zgrubnego oszacowania. Jeśli masz kilka połaci dachu o różnej orientacji lub częściowo ' +
        'zacienionych, firma rzemieślnicza w ofercie zaplanuje też odpowiedni podział elektryczny ' +
        '(np. na kilka wejść falownika) - to część profesjonalnego planowania, a nie coś, co musisz ' +
        'sam ustalić z góry.',
    },
  ],
  faq: [
    {
      q: 'Dlaczego czasami widzę "średnią ogólnokrajową" zamiast ceny regionalnej?',
      a: 'Ponieważ dla Twojego regionu nie ma jeszcze wystarczająco porównywalnych ofert. Gdy tylko więcej firm rzemieślniczych z Twojego regionu złoży oferty, obliczenia automatycznie przełączą się na dokładniejszą średnią regionalną.',
    },
    {
      q: 'Czy pokazana liczba to wiążąca oferta?',
      a: 'Nie. To szacunek na podstawie dotychczasowych ofert Roofora, a nie wiążąca cena dla Twojego konkretnego dachu. Wiążącą ofertę otrzymasz dopiero od firmy rzemieślniczej po prawdziwym zapytaniu.',
    },
    {
      q: 'Dlaczego przy samym kalkulatorze nie ma ocen w gwiazdkach?',
      a: 'Ponieważ nie zmyślamy ocen. Gdy tylko pojawią się prawdziwe, jawne oceny użytkowników, zostaną pokazane transparentnie - nie wcześniej.',
    },
    {
      q: 'Czy zacienienie mojego dachu jest uwzględniane w automatycznym oszacowaniu?',
      a: 'Nie - automatyczne oszacowanie uwzględnia tylko orientację, kąt nachylenia i lokalizację, nie indywidualne zacienienie. Rzeczywistą ocenę na podstawie zdjęć Twojego dachu przeprowadza firma rzemieślnicza odpowiadająca na zapytanie, w ramach swojej oferty.',
    },
  ],
  primaryCta: { label: 'Wypróbuj kalkulator kosztów teraz', action: 'customer' },
  secondaryCta: {
    label: 'Koszt instalacji fotowoltaicznej: pełny przewodnik',
    action: 'link',
    href: '/koszt-instalacji-fotowoltaicznej',
  },
  relatedLinks: [
    { label: 'Koszt instalacji fotowoltaicznej: pełny przewodnik', href: '/koszt-instalacji-fotowoltaicznej' },
    { label: 'Jak działa porównanie ofert', href: '/porownaj-oferty-fotowoltaiczne' },
    { label: 'Zaplanuj instalację krok po kroku', href: '/zaplanuj-instalacje-fotowoltaiczna' },
  ],
}
